import { useCallback } from "react";
import { useLocation } from "react-router";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { IconButton, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import cx from "classnames";
import AssetsFilters from "src/components/features/medium/AssetsFilter/AssetsFilters";
import AssetsFilterEnv from "src/components/features/medium/AssetsFilter/components/AssetsFilterEnv/AssetsFilterEnv";
import { useGlobalLocalContext } from "src/storage/localStorage.ts";
import DefinityIconSvg from "@images/definity-icon.svg";
import { appRoutes } from "@utils/appRoutes/appRoutes";
import { Breadcrumbs } from "./components/Breadcrumbs/Breadcrumbs";
import DbProxySelect from "./components/DbProxySelect/DbProxySelect";
import { SearchModal } from "./components/SearchModal/SearchModal";
import SystemSettings from "./components/SystemSettings/SystemSettings";
import TopbarComparativeAnalysisForm from "./components/TopBarComparativeAnalysisForm/TopBarComparativeAnalysisForm";
import TopbarDebugSettings from "./components/TopbarDebugSettings/TopbarDebugSettings";
import TopbarLightDarkSwitch from "./components/TopbarLightDarkSwitch/TopbarLightDarkSwitch";
import TopbarUser from "./components/TopbarUser/TopbarUser";
import { useEnv, useSystemSettings } from "./Topbar.hooks";

export const Topbar = () => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { env, switchEnv } = useEnv();
  const { systemSettingsQuery, toggleSystemSettings, isSystemSettingsOpen } =
    useSystemSettings();

  const { isSearchOpen, openSearch, closeSearch } = SearchModal.useSearchModal();
  const isDebugMode = useGlobalLocalContext((s) => s.isDebugMode);

  const openDocs = useCallback(() => {
    // biome-ignore lint: ok
    window.open("https://docs.definity.ai", "_blank");
  }, []);

  const refreshData = useCallback(() => {
    void queryClient.invalidateQueries();
  }, [queryClient]);

  const isAutoTestsEnabled = systemSettingsQuery.data?.auto_tests_enabled;
  const isCompareRunsPath = appRoutes["compare-runs"].matcher(pathname);

  return (
    <div
      data-test-id="top-bar"
      className={cx(
        "top-bar",
        "flex items-center justify-between z-100 py-2 bg-background-paper border-b default-border-color min-w-0 sticky top-0 h-(--top-bar-height)"
      )}
    >
      <div className={cx("pl-3")}>
        <DefinityIconSvg className="h-6 w-6" />
      </div>

      {import.meta.env.DEV && isDebugMode && (
        <div className="pl-4">
          <DbProxySelect />
        </div>
      )}

      {!isCompareRunsPath && (
        <div className="w-46 pl-4">
          <AssetsFilterEnv env={env} onChange={switchEnv} className="flex-1" />
        </div>
      )}

      <div className="flex-1 ml-6 min-w-0">
        {isCompareRunsPath ? <TopbarComparativeAnalysisForm /> : <Breadcrumbs />}
      </div>

      <div className="m-auto">
        <SearchModal.Button onClick={openSearch} />
      </div>

      <div className="ml-auto flex items-center z-20 pr-1">
        {!isCompareRunsPath && <AssetsFilters />}

        <Tooltip title="Refresh data">
          <IconButton
            onClick={refreshData}
            data-ph-capture-attribute-action="refresh_data"
          >
            <LoopOutlinedIcon />
          </IconButton>
        </Tooltip>
        <TopbarLightDarkSwitch />
        <Tooltip title="Open docs">
          <IconButton onClick={openDocs}>
            <ArticleOutlinedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Settings">
          <IconButton
            onClick={toggleSystemSettings}
            data-ph-capture-attribute-action="open_settings"
          >
            <SettingsOutlinedIcon />
          </IconButton>
        </Tooltip>

        <TopbarDebugSettings isAutoTestsEnabled={isAutoTestsEnabled} />
        <TopbarUser />
      </div>
      {isSystemSettingsOpen && <SystemSettings />}
      <SearchModal open={isSearchOpen} onClose={closeSearch} />
    </div>
  );
};
